import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../lib/utils';

/** DESIGN.md §6 Card: hairline border, no shadow. Padding is passed by the caller. */
export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('rounded-lg border border-[var(--line)] bg-[var(--surface)]', className)}
      {...props}
    />
  );
}

/** Card title block: title + one-line description + optional right-aligned action. */
export function CardTitle({
  title,
  description,
  action,
  as: Heading = 'h2',
  id,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  as?: 'h2' | 'h3';
  id?: string;
  className?: string;
}) {
  return (
    <div className={cn('mb-4 flex items-start justify-between gap-4', className)}>
      <div className="min-w-0">
        <Heading id={id} className="text-sm font-semibold text-[var(--ink)]">
          {title}
        </Heading>
        {description ? (
          <p className="mt-0.5 text-xs text-[var(--ink-soft)]">{description}</p>
        ) : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

export type StatTone = 'neutral' | 'positive' | 'negative';

const toneClass: Record<StatTone, string> = {
  neutral: 'text-[var(--ink)]',
  positive: 'text-[var(--positive)]',
  negative: 'text-[var(--negative)]',
};

/** DESIGN.md §6 StatCard: label → description → value → optional hint. */
export function StatCard({
  label,
  description,
  value,
  hint,
  tone = 'neutral',
  hero = false,
  className,
}: {
  label: ReactNode;
  description?: ReactNode;
  value: ReactNode;
  hint?: ReactNode;
  tone?: StatTone;
  /** Hero number (`text-4xl`), used once per dashboard. */
  hero?: boolean;
  className?: string;
}) {
  return (
    <Card className={cn('flex flex-col p-5', className)}>
      <p className="text-xs font-medium text-[var(--ink)]">{label}</p>
      {description ? <p className="text-xs text-[var(--ink-soft)]">{description}</p> : null}
      <p
        className={cn(
          'mt-3 font-semibold tracking-tight whitespace-nowrap tabular-nums',
          hero ? 'mt-auto pt-6 text-4xl' : 'text-lg lg:text-xl',
          toneClass[tone],
        )}
      >
        {value}
      </p>
      {hint ? <p className="mt-1 text-xs text-[var(--ink-faint)]">{hint}</p> : null}
    </Card>
  );
}
